import { capitalize } from '@/domain/catalog/catalog-status-text';
import { thumbPath } from '@/domain/catalog/catalog-view';
import type {
  IAssistantAffectedGroup,
  IAssistantExcludedRow,
  IAssistantProposalView,
  IAssistantRow,
  IPriceValue,
  ISizesValue,
  TAssistantKind,
  TAssistantOpName,
  TRowEntity,
  TRowField,
  TRowReason,
} from '@/interfaces/assistant.interface';
import type {
  IGhDiffCard,
  IGhDiffNote,
  IGhDiffReview,
  IGhDiffRow,
  IGhDiffValue,
  IGhExpandAction,
  IGhHostAction,
  IGhPayload,
  IGhPromptAction,
} from '../gpthelp-host-contract';
import {
  bidiIsolateParams,
  cardTextLength,
  clampText,
  fitSentences,
  fitText,
  hostPlural,
  joinSentences,
  sentenceRoom,
} from './card-text';
import type { ICardFormat } from './card-text';
import {
  affectedGroupByRow,
  changesSharedGroup,
  grownGroupText,
  ownRulesCount,
  usedInText,
} from './affected-cards';
import type { TStillHiddenReason } from './availability-context';
import { cardId } from './card-ids';
import { knownKind, ofKind } from './card-kinds';
import {
  changedGroupId,
  GENERIC_PRESENTER,
  groupLinkChange,
  isNewGroupRow,
  isProductRow,
  isSizeRow,
  isStockRow,
  linkKey,
  paidPrice,
  presenterOf,
  rowEntity,
  rowLabel,
  rowLabelWithField,
} from './row-presenters';
import type { IRowPresenter, TGroupLink } from './row-presenters';
import {
  isFlagged,
  requiredKeys,
  rowsNeedingUnticked,
} from './row-requirements';
import {
  addsOtherThanProducts,
  addsProducts,
  isAvailabilityValue,
  isCategoryExclusion,
  isCopyValue,
  isCreateValue,
  isFlagValue,
  isNewCategoryValue,
  isNewChoiceValue,
  isNewProductExclusion,
  isPlacementValue,
  isPriceValue,
  isScheduleValue,
  isSizesValue,
  productCount,
  rowItemLabel,
  singleProductRow,
} from './row-values';

/** What the proposal card of a view needs besides the view (plan §9.9). */
export interface IProposalCardInput {
  view: IAssistantProposalView;
  /** The user's request, shown in the review («Pedido: «…»»); null: none. */
  requestText: string | null;
  /** The store has several branches: availability changes say where. */
  multiBranch: boolean;
  /** `Globals.uploadsPath`: the product picture of a single-row card. */
  uploadsPath: string;
  /**
   * By row key: what still hides from customers here something the row turns
   * on (its category, menu or hours; plan P0-3). Rows without reasons are
   * absent.
   */
  stillHidden: Record<string, TStillHiddenReason[]>;
  /**
   * The products (ids) whose rows change which of their sizes is the first
   * on sale, or its price, so their price follows it (plan P4-1,
   * `sizes-context.ts`); a switch between one price and sizes is not
   * listed (its own row says so).
   */
  firstSizeChangedIds: number[];
}

/**
 * `inline`: every row on the card with «Aplicar»; `collapsed`: the first
 * rows and «Revisar los {n} cambios», which opens the review.
 */
export type TProposalMode = 'inline' | 'collapsed';

/** Host contract §3.3 caps. */
const TITLE_MAX = 120;
const META_MAX = 120;
const EYEBROW_MAX = 40;
const REQUEST_MAX = 500;
const NOTE_MAX = 240;
/**
 * A product name the note shortens keeps at least this many characters
 * («…» included); with less room its sentence is left out.
 */
const NOTE_NAME_MIN = 16;
const SUMMARY_MAX = 240;
const BANNER_MAX = 200;
const LABEL_MAX = 40;
const ROW_TITLE_MAX = 160;
const SUBTITLE_MAX = 120;
const REASON_MAX = 120;
const PILL_MAX = 24;
const THUMB_MAX = 1000;
const PROMPT_TEXT_MAX = 500;
const EMPTY_SELECTION_MAX = 160;

/** A card shows every row inline up to this many (plan §9.9 ladder). */
const INLINE_MAX_ROWS = 5;
/** The widget shows «y {n} productos más» past this many rows. */
const MORE_AFTER_ROWS = 3;

/** An empty text value (a description that was or becomes empty). */
const EMPTY_TEXT = '—';

const PRICE_CODES = ['setPrice', 'adjustPrices', 'setDiscount', 'setPrices'];

/** Exclusions whose price cannot be changed this way (`proposal.note.excluded`). */
const PRICE_EXCLUSIONS: Array<IAssistantExcludedRow['reason']> = [
  'DISCOUNT_INVALID',
  'PRICE_INVALID',
  'NO_BASE_PRICE',
];

/**
 * Fields whose single-row title does not name the subject (a new product, a
 * product moved to another category, a product that gets or loses an option
 * group: plan P3-4; a copy, titled by the product it copies: plan P4-3; a
 * stock item's movement, count or creation, titled by how many: plan P5-3):
 * the row names it instead.
 */
const SUBJECT_IN_ROW_FIELDS: readonly TRowField[] = [
  'create',
  'category',
  'groups',
  'copy',
  'stockMove',
  'stockCount',
  'newStockItem',
];

/** Rows that create something: a product, a category or a copy of a product. */
const CREATION_FIELDS: readonly TRowField[] = ['create', 'newCategory', 'copy'];

const OUTLIER_REASON_KEYS: Partial<Record<TRowReason, string>> = {
  BIG_INCREASE: 'assistant.proposal.row.bigIncrease',
  BIG_DECREASE: 'assistant.proposal.row.bigDecrease',
  ROUNDING_STEP: 'assistant.proposal.row.roundingStep',
  STOCK_NEGATIVE: 'assistant.proposal.row.stockNegative',
};

/**
 * What a name that is taken reads, by the row's entity (plan P4-1): a
 * category's, a group's, a size's or a stock item's (P5-3); a product's
 * otherwise.
 */
const NAME_EXISTS_KEYS: Readonly<Partial<Record<TRowEntity, string>>> = {
  category: 'assistant.proposal.row.categoryNameExists',
  group: 'assistant.proposal.row.groupNameExists',
  size: 'assistant.proposal.row.sizeNameExists',
  stockItem: 'assistant.proposal.row.stockNameExists',
};

/** «Ya hay un producto con ese nombre». */
const NAME_EXISTS_KEY = 'assistant.proposal.row.nameExists';

/** «Puede deshacerlo.» */
const UNDO_KEY = 'assistant.proposal.note.undo';

/** «La copia queda oculta en todas las sucursales…» (plan P4-3). */
const COPY_HIDDEN_KEY = 'assistant.proposal.note.copyHidden';

/** «Fuera de esos horarios no se ve en la carta…» (plan P4-2). */
const SCHEDULE_KEY = 'assistant.proposal.note.schedule';

/** «El precio del producto es el de su primera variante disponible…» (plan P4-1). */
const SIZES_KEY = 'assistant.proposal.note.sizes';

/** The id of a proposal's card; every result card of it keeps this id. */
export function proposalCardId(proposalId: string): string {
  return cardId('proposal', proposalId);
}

/**
 * A proposal with rows to apply. A refresh can leave none (every product
 * changed or is gone): such a proposal has nothing to apply.
 */
export function hasRowsToApply(
  view: Pick<IAssistantProposalView, 'rows' | 'rowCount'>
): boolean {
  return view.rowCount > 0 && view.rows.length > 0;
}

/**
 * Inline when there are at most 5 rows, none flagged, and no customer text
 * behind it; anything else goes through the review.
 */
export function proposalMode(view: IAssistantProposalView): TProposalMode {
  return view.rows.length <= INLINE_MAX_ROWS &&
    !view.tainted &&
    !view.rows.some(isFlagged)
    ? 'inline'
    : 'collapsed';
}

/** Row keys never hold a comma (plan F6 key grammar). */
const REQUIRED_SEPARATOR = ',';

/**
 * What every action of a proposal's cards carries: the proposal and the view
 * it was shown with, where it was made (scope check), its row count, whether
 * it adds products (an undone one says they were removed) or something else
 * (`createdAny`, when it does: a category, an option group or a choice),
 * whether it changes more than products (`speaksOfChanges`, when it does:
 * the cards that replace it speak of changes, plan P2-4), the rows other
 * rows need (`required`, when any: the apply checks them, plan P2-3), and
 * its kind when it is not a change of the menu (plan P5-3: an inventory
 * change's receipt has its own title and page; absent = a change of the
 * menu, as on the cards written before).
 */
export function proposalPayload(view: IAssistantProposalView): IGhPayload {
  const payload: IGhPayload = {
    proposalId: view.id,
    viewHash: view.viewHash,
    restaurantId: view.restaurantId,
    branchId: view.branchId,
    branchName: view.branchName,
    rows: view.rowCount,
    created: addsProducts(view.rows),
  };
  if (view.kind !== 'menu.changes') payload.kind = view.kind;
  if (addsOtherThanProducts(view.rows)) payload.createdAny = true;
  if (speaksOfChanges(view)) payload.speaksOfChanges = true;
  const required = requiredKeys(view.rows);
  if (required.length) payload.required = required.join(REQUIRED_SEPARATOR);
  return payload;
}

/**
 * The kind of the proposal whose card wrote `payload` (`proposalPayload`):
 * the kind it names; any other payload, older cards included, is a change
 * of the menu.
 */
export function payloadKind(payload: IGhPayload): TAssistantKind {
  return knownKind(payload.kind);
}

/** The keys of the rows other rows need, as `proposalPayload` wrote them; [] for none. */
export function payloadRequiredKeys(payload: IGhPayload): string[] {
  const { required } = payload;
  return typeof required === 'string' && required !== ''
    ? required.split(REQUIRED_SEPARATOR)
    : [];
}

/** What the change is about: one product, the categories, the whole menu or «{n} productos». */
function targetText(view: IAssistantProposalView, fmt: ICardFormat): string {
  const product = singleProductRow(view.rows);
  if (product) return product.itemName;
  const { summary } = view;
  if (summary.targetAll) return fmt.t('assistant.proposal.target.all');
  const categories = summary.categoryNames ?? [];
  if (categories.length >= 3)
    return fmt.tc('assistant.proposal.target.categories', categories.length);
  if (categories.length) return fmt.list(categories);
  return fmt.tc('assistant.proposal.target.items', productCount(view.rows));
}

/**
 * The categories a change of categories is about, by the names of its rows
 * (each row is a category, so one left out is never named): by name,
 * «{n} categorías» from 3 on.
 */
function categoryTarget(
  view: IAssistantProposalView,
  fmt: ICardFormat
): string {
  const names = Array.from(new Set(view.rows.map((row) => row.itemName)));
  if (names.length >= 3)
    return fmt.tc('assistant.proposal.target.categories', names.length);
  return fmt.list(names);
}

/**
 * The proposal changes more than products (a category, a menu…) or leaves a
 * category out: its review hint, banners, count, rows past the first ones
 * («y 3 cambios más») and left-out note speak of changes (plan P2-4).
 * Product-only proposals keep the product words.
 */
function speaksOfChanges(view: IAssistantProposalView): boolean {
  return (
    !view.rows.every(isProductRow) || view.excluded.some(isCategoryExclusion)
  );
}

/**
 * The rows read as changes, not products: the proposal speaks of changes, or
 * a row changes a product's variants (plan P4-1: several rows of one product
 * are each a change of it, W21 B3). Its review hint, banners and rows past
 * the first ones then say «cambios»; its count still counts the products.
 */
function rowsAreChanges(view: IAssistantProposalView): boolean {
  return speaksOfChanges(view) || view.rows.some(isSizeRow);
}

/** The count title of each kind (`mixedTitle`). */
const MIXED_TITLE_KEYS: Readonly<Record<TAssistantKind, string>> = {
  'menu.changes': 'assistant.proposal.title.mixed',
  'stock.changes': 'assistant.proposal.title.mixedStock',
};

/**
 * «1 cambio en su menú», «3 cambios en su menú»; an inventory change, which
 * changes nothing on the menu: «3 cambios de inventario» (plan P5-3).
 */
function mixedTitle(
  count: number,
  fmt: ICardFormat,
  kind: TAssistantKind = 'menu.changes'
): string {
  return clampText(
    fmt.tc(ofKind(MIXED_TITLE_KEYS, kind), count, { n: fmt.number(count) }),
    TITLE_MAX
  );
}

/** «Crear 1 categoría», «Crear 2 categorías». */
function createCategoryTitle(count: number, fmt: ICardFormat): string {
  return clampText(
    fmt.tc('assistant.proposal.title.createCategory', count, {
      n: fmt.number(count),
    }),
    TITLE_MAX
  );
}

/** «Mover 3 productos a Platos del día». */
function moveTitle(count: number, category: string, fmt: ICardFormat): string {
  return fitText(
    fmt,
    'assistant.proposal.title.moveItems',
    { category, n: fmt.number(count) },
    'category',
    TITLE_MAX,
    count
  );
}

/** «Solo en el Punto de Venta: Postres» / «Mostrar Postres en la carta». */
function posOnlyTitle(on: boolean, name: string, fmt: ICardFormat): string {
  return fitText(
    fmt,
    on
      ? 'assistant.proposal.title.posOnlyOn'
      : 'assistant.proposal.title.posOnlyOff',
    { name },
    'name',
    TITLE_MAX
  );
}

/**
 * The one value every row shares, or undefined when they differ (or a row
 * holds none).
 */
function sharedValue<T>(
  rows: IAssistantRow[],
  valueOf: (row: IAssistantRow) => T | undefined
): T | undefined {
  const values = rows.map(valueOf);
  const [first] = values;
  return first !== undefined && values.every((value) => value === first)
    ? first
    : undefined;
}

/** «Crear el grupo Leche» (plan P3-4). */
function createGroupTitle(name: string, fmt: ICardFormat): string {
  return fitText(
    fmt,
    'assistant.proposal.title.createOptionGroup',
    { name },
    'name',
    TITLE_MAX
  );
}

/**
 * «Cambiar el grupo Leches», «Agregar opciones a Leches», «Ordenar las
 * opciones de Cortado»: a change of one group's content, or of the order of
 * one product's groups (plan P3-4).
 */
function optionsTitle(key: string, name: string, fmt: ICardFormat): string {
  return fitText(fmt, key, { name }, 'name', TITLE_MAX);
}

/** «Agregar Leche a 8 productos» / «Quitar Leche de 3 productos» (plan P3-4). */
function linkTitle(
  kind: 'added' | 'removed',
  group: string,
  count: number,
  fmt: ICardFormat
): string {
  return fitText(
    fmt,
    kind === 'added'
      ? 'assistant.proposal.title.linkOptionGroup'
      : 'assistant.proposal.title.unlinkOptionGroup',
    { group, n: fmt.number(count) },
    'group',
    TITLE_MAX,
    count
  );
}

/** «Ordenar las opciones de {name}»: a group's choices, or a product's groups. */
const REORDER_OPTIONS_KEY = 'assistant.proposal.title.reorderOptions';
/** «Cambiar el grupo {name}». */
const UPDATE_GROUP_KEY = 'assistant.proposal.title.updateOptionGroup';

/**
 * The title of a proposal whose rows all change one existing group's content,
 * by its code (plan P3-4): a group's own changes and a choice's are a change
 * of the group, as is any mix of them.
 */
const GROUP_CONTENT_TITLE_KEYS: Readonly<
  Partial<Record<TAssistantOpName | 'mixed', string>>
> = {
  updateOptionGroup: UPDATE_GROUP_KEY,
  updateOptionChoice: UPDATE_GROUP_KEY,
  addOptionChoices: 'assistant.proposal.title.addOptionChoices',
  removeOptionChoices: 'assistant.proposal.title.removeOptionChoices',
  reorderOptionChoices: REORDER_OPTIONS_KEY,
  mixed: UPDATE_GROUP_KEY,
};

/** The title of one row of a group's content, by its field (plan P3-4). */
function groupRowTitleKey(field: TRowField): string {
  switch (field) {
    case 'newChoice':
      return 'assistant.proposal.title.addOptionChoices';
    case 'remove':
      return 'assistant.proposal.title.removeOptionChoices';
    case 'order':
      return REORDER_OPTIONS_KEY;
    default:
      return UPDATE_GROUP_KEY;
  }
}

/**
 * The title of one row of option groups (plan P3-4), routed before the
 * product titles (a group's name is not a product's): a new group, a change
 * of a group's content, or a product that gets or loses one group or
 * reorders its groups. Undefined for any other row.
 */
function optionRowTitle(
  row: IAssistantRow,
  fmt: ICardFormat
): string | undefined {
  if (isNewGroupRow(row)) return createGroupTitle(row.itemName, fmt);
  if (changedGroupId(row) !== null)
    return optionsTitle(groupRowTitleKey(row.field), row.itemName, fmt);
  if (row.field !== 'groups' || rowEntity(row) !== 'item') return undefined;
  const change = groupLinkChange(row);
  if (!change) return mixedTitle(1, fmt);
  if (change.kind === 'reordered')
    return optionsTitle(REORDER_OPTIONS_KEY, row.itemName, fmt);
  return linkTitle(change.kind, change.group.name, 1, fmt);
}

/**
 * The one group every row adds to its product (or takes out of it), and
 * nothing else; undefined when the rows differ.
 */
function sharedLinkedGroup(
  rows: IAssistantRow[],
  kind: 'added' | 'removed'
): TGroupLink | undefined {
  let shared: TGroupLink | undefined;
  for (const row of rows) {
    const change = groupLinkChange(row);
    if (!change || change.kind === 'reordered' || change.kind !== kind)
      return undefined;
    if (shared && linkKey(shared) !== linkKey(change.group)) return undefined;
    shared = change.group;
  }
  return shared;
}

/**
 * The title of a proposal of option groups, by its code (plan P3-4);
 * undefined for any other proposal, or when its rows do not share what the
 * title names (it then counts its changes). A copy of a group (a new group,
 * the old one taken out of the products) mixes kinds: «{n} cambios en su
 * menú».
 */
function optionGroupsTitle(
  view: IAssistantProposalView,
  fmt: ICardFormat
): string | undefined {
  const { rows } = view;
  const { code } = view.summary;
  switch (code) {
    case 'createOptionGroup': {
      const created = rows.filter(isNewGroupRow);
      return created.length === 1
        ? createGroupTitle(created[0].itemName, fmt)
        : undefined;
    }
    case 'linkOptionGroup':
    case 'unlinkOptionGroup': {
      const kind = code === 'linkOptionGroup' ? 'added' : 'removed';
      const group = sharedLinkedGroup(rows, kind);
      return group
        ? linkTitle(kind, group.name, productCount(rows), fmt)
        : undefined;
    }
    case 'reorderOptionGroups': {
      const product = singleProductRow(rows);
      return product
        ? optionsTitle(REORDER_OPTIONS_KEY, product.itemName, fmt)
        : undefined;
    }
    default: {
      const key = GROUP_CONTENT_TITLE_KEYS[code];
      if (!key) return undefined;
      const group = sharedValue(
        rows,
        (row) => changedGroupId(row) ?? undefined
      );
      return group === undefined
        ? undefined
        : optionsTitle(key, rows[0].itemName, fmt);
    }
  }
}

/** «Variantes de Napolitana», «Variantes de 3 productos» (plan P4-1). */
function sizesTitle(name: string, fmt: ICardFormat): string {
  return fitText(
    fmt,
    'assistant.proposal.title.sizes',
    { name },
    'name',
    TITLE_MAX
  );
}

/** «Horarios de Menú ejecutivo», «Horarios de Postres y Bebidas» (plan P4-2). */
function scheduleTitle(target: string, fmt: ICardFormat): string {
  return fitText(
    fmt,
    'assistant.proposal.title.schedule',
    { target },
    'target',
    TITLE_MAX
  );
}

/** «Duplicar Milanesa napolitana»: the product copied (plan P4-3). */
function duplicateTitle(name: string, fmt: ICardFormat): string {
  return fitText(
    fmt,
    'assistant.proposal.title.duplicate',
    { name },
    'name',
    TITLE_MAX
  );
}

/** «Notas de preparación de Bife de chorizo» (plan P4-4). */
function notesTitle(name: string, fmt: ICardFormat): string {
  return fitText(
    fmt,
    'assistant.proposal.title.notes',
    { name },
    'name',
    TITLE_MAX
  );
}

/** One product by its name, or «{n} productos». */
function productsTarget(rows: IAssistantRow[], fmt: ICardFormat): string {
  const product = singleProductRow(rows);
  if (product) return product.itemName;
  return fmt.tc('assistant.proposal.target.items', productCount(rows));
}

/**
 * The title of one row of Phase 4 (plan P4-1 to P4-4), routed before the
 * product titles (a size's name or pause is not the product's): the
 * product's sizes, the hours of a product or category, a copy (by the
 * product it copies) or preparation notes. Undefined for any other row.
 */
function phase4RowTitle(
  row: IAssistantRow,
  fmt: ICardFormat
): string | undefined {
  if (isSizeRow(row)) return sizesTitle(row.itemName, fmt);
  switch (row.field) {
    case 'schedule':
      return scheduleTitle(row.itemName, fmt);
    case 'copy':
      return isCopyValue(row.to)
        ? duplicateTitle(row.to.sourceName, fmt)
        : undefined;
    case 'notes':
      return notesTitle(row.itemName, fmt);
    default:
      return undefined;
  }
}

/**
 * The title of a proposal of Phase 4 by its rows, whatever ops they came
 * from (plan P4-1 to P4-4): every row changes sizes, hours (of products or
 * of categories, not both), copies products («Duplicar» one, else the
 * products it adds) or preparation notes. Undefined for any other proposal.
 */
function phase4Title(
  view: IAssistantProposalView,
  fmt: ICardFormat
): string | undefined {
  const { rows } = view;
  if (!rows.length) return undefined;
  if (rows.every(isSizeRow)) return sizesTitle(productsTarget(rows, fmt), fmt);
  const every = (field: TRowField) => rows.every((row) => row.field === field);
  if (every('schedule')) {
    const entity = sharedValue(rows, rowEntity);
    if (entity === 'category')
      return scheduleTitle(categoryTarget(view, fmt), fmt);
    return entity === 'item'
      ? scheduleTitle(productsTarget(rows, fmt), fmt)
      : undefined;
  }
  if (every('copy'))
    return rows.length === 1
      ? phase4RowTitle(rows[0], fmt)
      : clampText(
          fmt.tc('assistant.proposal.title.create', rows.length),
          TITLE_MAX
        );
  if (every('notes')) return notesTitle(productsTarget(rows, fmt), fmt);
  return undefined;
}

/** Fields of a purchase, a loss or a physical count (plan P5-1 recordStock). */
const STOCK_MOVE_FIELDS: readonly TRowField[] = ['stockMove', 'stockCount'];

/** Fields of an existing stock item's own edits (plan P5-1 updateStockItem). */
const STOCK_EDIT_FIELDS: readonly TRowField[] = [
  'name',
  'unit',
  'cost',
  'minimum',
];

/** «Registrar 3 movimientos de stock» (plan P5-3). */
function recordStockTitle(count: number, fmt: ICardFormat): string {
  return clampText(
    fmt.tc('assistant.proposal.title.recordStock', count, {
      n: fmt.number(count),
    }),
    TITLE_MAX
  );
}

/** «Crear 2 insumos» (plan P5-3). */
function createStockItemTitle(count: number, fmt: ICardFormat): string {
  return clampText(
    fmt.tc('assistant.proposal.title.createStockItem', count, {
      n: fmt.number(count),
    }),
    TITLE_MAX
  );
}

/** «Cambiar Aceite»: a stock item's own fields (plan P5-3). */
function updateStockItemTitle(name: string, fmt: ICardFormat): string {
  return fitText(
    fmt,
    'assistant.proposal.title.updateStockItem',
    { name },
    'name',
    TITLE_MAX
  );
}

/**
 * «Receta de Pizza margarita»: the recipe of a product, of one of its sizes
 * or of an option choice, titled by the product or the choice's group like
 * their prices; the row names the size or the choice (plan P5-3).
 */
function recipeTitle(name: string, fmt: ICardFormat): string {
  return fitText(
    fmt,
    'assistant.proposal.title.setRecipe',
    { name },
    'name',
    TITLE_MAX
  );
}

/**
 * Every row edits a field of the same existing stock item (plan P5-1
 * updateStockItem): the title names it, and each row its field.
 */
function editsOneStockItem(rows: IAssistantRow[]): boolean {
  return (
    rows.length > 0 &&
    rows.every(
      (row) =>
        rowEntity(row) === 'stockItem' && STOCK_EDIT_FIELDS.includes(row.field)
    ) &&
    sharedValue(rows, (row) => row.stockItemId ?? undefined) !== undefined
  );
}

/**
 * The title of one inventory row (plan P5-3), routed before the product
 * titles (a stock item's name is not a product's): a movement or a count, a
 * new stock item, a stock item's own field, or a recipe. Undefined for any
 * other row.
 */
function stockRowTitle(
  row: IAssistantRow,
  fmt: ICardFormat
): string | undefined {
  if (!isStockRow(row)) return undefined;
  if (rowEntity(row) === 'recipe') return recipeTitle(row.itemName, fmt);
  if (STOCK_MOVE_FIELDS.includes(row.field)) return recordStockTitle(1, fmt);
  if (row.field === 'newStockItem') return createStockItemTitle(1, fmt);
  return STOCK_EDIT_FIELDS.includes(row.field)
    ? updateStockItemTitle(row.itemName, fmt)
    : undefined;
}

/**
 * The title of an inventory proposal by its rows, whatever ops they came
 * from (plan P5-3): every row records a movement or a count, creates a stock
 * item, edits one stock item, or sets the recipe of one product (its sizes
 * included) or one choice. Undefined for any other proposal: it counts its
 * changes («3 cambios de inventario» for an inventory change).
 */
function stockTitle(
  view: IAssistantProposalView,
  fmt: ICardFormat
): string | undefined {
  const { rows } = view;
  if (!rows.length || !rows.every(isStockRow)) return undefined;
  const every = (fields: readonly TRowField[]) =>
    rows.every((row) => fields.includes(row.field));
  if (every(STOCK_MOVE_FIELDS)) return recordStockTitle(rows.length, fmt);
  if (every(['newStockItem'])) return createStockItemTitle(rows.length, fmt);
  if (editsOneStockItem(rows))
    return updateStockItemTitle(rows[0].itemName, fmt);
  if (rows.every((row) => rowEntity(row) === 'recipe')) {
    const subject = singleProductRow(rows);
    return subject ? recipeTitle(subject.itemName, fmt) : undefined;
  }
  return undefined;
}

/**
 * The title of a proposal of `kind` with a single row, whatever ops it came
 * from. A row this release does not know is «1 cambio en su menú» («1
 * cambio de inventario» in an inventory change), never «Agregar».
 */
function singleRowTitle(
  row: IAssistantRow,
  fmt: ICardFormat,
  kind: TAssistantKind
): string {
  if (presenterOf(row) === GENERIC_PRESENTER) return mixedTitle(1, fmt, kind);
  const options = optionRowTitle(row, fmt);
  if (options !== undefined) return options;
  const phase4 = phase4RowTitle(row, fmt);
  if (phase4 !== undefined) return phase4;
  const stock = stockRowTitle(row, fmt);
  if (stock !== undefined) return stock;
  const name = { name: row.itemName };
  const category = rowEntity(row) === 'category';
  switch (row.field) {
    case 'price':
      return fitText(
        fmt,
        'assistant.proposal.title.setPrice',
        name,
        'name',
        TITLE_MAX
      );
    case 'availability':
      return fitText(
        fmt,
        isAvailabilityValue(row.to) && row.to.available
          ? 'assistant.proposal.title.show'
          : 'assistant.proposal.title.hide',
        { target: row.itemName },
        'target',
        TITLE_MAX
      );
    case 'name':
      return fitText(
        fmt,
        category
          ? 'assistant.proposal.title.renameCategory'
          : 'assistant.proposal.title.rename',
        name,
        'name',
        TITLE_MAX
      );
    case 'description':
      return fitText(
        fmt,
        category
          ? 'assistant.proposal.title.categoryDescription'
          : 'assistant.proposal.title.description',
        name,
        'name',
        TITLE_MAX
      );
    case 'create':
      return clampText(fmt.tc('assistant.proposal.title.create', 1), TITLE_MAX);
    case 'newCategory':
      return createCategoryTitle(1, fmt);
    case 'posOnly':
      return isFlagValue(row.to)
        ? posOnlyTitle(row.to.on, row.itemName, fmt)
        : mixedTitle(1, fmt, kind);
    case 'category':
      return isPlacementValue(row.to)
        ? moveTitle(1, row.to.categoryName, fmt)
        : mixedTitle(1, fmt, kind);
    case 'order':
      return rowEntity(row) === 'menu'
        ? clampText(
            fmt.t('assistant.proposal.title.reorderCategories'),
            TITLE_MAX
          )
        : fitText(
            fmt,
            'assistant.proposal.title.reorderItems',
            name,
            'name',
            TITLE_MAX
          );
    default:
      return mixedTitle(1, fmt, kind);
  }
}

/** «Subir 10 % Bebidas», «Bajar $ 500 toda la carta». */
function adjustTitle(view: IAssistantProposalView, fmt: ICardFormat): string {
  const { percent, amount } = view.summary;
  const target = targetText(view, fmt);
  if (percent !== undefined)
    return fitText(
      fmt,
      percent < 0
        ? 'assistant.proposal.title.lower'
        : 'assistant.proposal.title.raise',
      { percent: fmt.number(Math.abs(percent)), target },
      'target',
      TITLE_MAX
    );
  if (amount !== undefined)
    return fitText(
      fmt,
      amount < 0
        ? 'assistant.proposal.title.lowerAmount'
        : 'assistant.proposal.title.raiseAmount',
      { amount: fmt.money(Math.abs(amount)), target },
      'target',
      TITLE_MAX
    );
  return fitText(
    fmt,
    'assistant.proposal.title.setPrice',
    { name: target },
    'name',
    TITLE_MAX
  );
}

/**
 * «Descuento de Medialuna», or «Quitar el descuento de Medialuna» when every
 * row removes it, for one product (its sizes too, like `targetText`);
 * «Descuentos en 3 productos» for several (plan P1-1), or «Quitar los
 * descuentos de 3 productos» when every row removes one.
 */
function discountTitle(view: IAssistantProposalView, fmt: ICardFormat): string {
  const { rows } = view;
  const removes =
    rows.length > 0 &&
    rows.every(
      (row) => isPriceValue(row.to) && row.to.discountedPrice === null
    );
  const product = singleProductRow(rows);
  if (product) {
    return fitText(
      fmt,
      removes
        ? 'assistant.proposal.title.clearDiscount'
        : 'assistant.proposal.title.setDiscount',
      { name: product.itemName },
      'name',
      TITLE_MAX
    );
  }
  const products = productCount(rows);
  return clampText(
    fmt.tc(
      removes
        ? 'assistant.proposal.title.clearDiscounts'
        : 'assistant.proposal.title.discounts',
      products,
      { n: fmt.number(products) }
    ),
    TITLE_MAX
  );
}

/** The card title, from the view's summary (plan §9.9 `proposal.title.*`). */
export function proposalTitle(
  view: IAssistantProposalView,
  fmt: ICardFormat
): string {
  const { summary, rows } = view;
  if (summary.code === 'mixed' && rows.length === 1)
    return singleRowTitle(rows[0], fmt, view.kind);
  const options = optionGroupsTitle(view, fmt);
  if (options !== undefined) return options;
  const phase4 = phase4Title(view, fmt);
  if (phase4 !== undefined) return phase4;
  const stock = stockTitle(view, fmt);
  if (stock !== undefined) return stock;
  switch (summary.code) {
    case 'setPrice':
      return fitText(
        fmt,
        'assistant.proposal.title.setPrice',
        { name: targetText(view, fmt) },
        'name',
        TITLE_MAX
      );
    case 'adjustPrices':
      return adjustTitle(view, fmt);
    case 'setDiscount':
      return discountTitle(view, fmt);
    case 'setPrices':
      return clampText(
        fmt.tc('assistant.proposal.title.setPrices', view.rowCount, {
          n: fmt.number(view.rowCount),
        }),
        TITLE_MAX
      );
    case 'setAvailability':
      if (summary.available === undefined) break;
      return fitText(
        fmt,
        summary.available
          ? 'assistant.proposal.title.show'
          : 'assistant.proposal.title.hide',
        { target: targetText(view, fmt) },
        'target',
        TITLE_MAX
      );
    // «Ocultar Postres», «Mostrar Postres y Bebidas» (plan P2-4).
    case 'setCategoryAvailability':
      if (summary.available === undefined) break;
      return fitText(
        fmt,
        summary.available
          ? 'assistant.proposal.title.show'
          : 'assistant.proposal.title.hide',
        { target: categoryTarget(view, fmt) },
        'target',
        TITLE_MAX
      );
    case 'createCategory':
      return createCategoryTitle(view.rowCount, fmt);
    // One category names it; several are «{n} cambios en su menú», like products.
    case 'renameCategory':
    case 'setCategoryDescription':
      if (rows.length !== 1) break;
      return singleRowTitle(rows[0], fmt, view.kind);
    case 'setCategoryPosOnly': {
      const on = sharedValue(rows, (row) =>
        isFlagValue(row.to) ? row.to.on : undefined
      );
      if (on === undefined) break;
      return posOnlyTitle(on, categoryTarget(view, fmt), fmt);
    }
    // «Mover 3 productos a Platos del día»: every product to one category.
    case 'moveItems': {
      const category = sharedValue(rows, (row) =>
        isPlacementValue(row.to) ? row.to.categoryName : undefined
      );
      if (category === undefined) break;
      return moveTitle(productCount(rows), category, fmt);
    }
    case 'reorderItems':
      return fitText(
        fmt,
        'assistant.proposal.title.reorderItems',
        { name: categoryTarget(view, fmt) },
        'name',
        TITLE_MAX
      );
    case 'reorderCategories':
      return clampText(
        fmt.t('assistant.proposal.title.reorderCategories'),
        TITLE_MAX
      );
    case 'renameItem':
    case 'setDescription': {
      const product = singleProductRow(rows);
      if (!product) break;
      return fitText(
        fmt,
        summary.code === 'renameItem'
          ? 'assistant.proposal.title.rename'
          : 'assistant.proposal.title.description',
        { name: product.itemName },
        'name',
        TITLE_MAX
      );
    }
    case 'createItem':
      return clampText(
        fmt.tc('assistant.proposal.title.create', view.rowCount),
        TITLE_MAX
      );
    default:
      break;
  }
  // One row left (the others excluded, or ops that disagree): its own title
  // names the product or category instead of «1 cambio en su menú».
  if (rows.length === 1) return singleRowTitle(rows[0], fmt, view.kind);
  // Several stock items, recipe targets or inventory ops: «3 cambios de
  // inventario» (plan P5-3).
  return mixedTitle(view.rowCount, fmt, view.kind);
}

/**
 * «12 productos · redondeo a $ 100» («5 cambios» when it changes more than
 * products, plan P2-4), and where an availability change applies in a store
 * with several branches (a product's or a category's, plan P2-4). A
 * single-row card names its subject in the title, so it shows only where.
 */
function proposalMeta(
  input: IProposalCardInput,
  fmt: ICardFormat
): string | null {
  const { view } = input;
  const { summary } = view;
  const parts: string[] = [];
  if (view.rows.length > 1 && speaksOfChanges(view)) {
    parts.push(fmt.tc('assistant.proposal.metaChanges', view.rows.length));
  } else if (view.rows.length > 1) {
    const products = productCount(view.rows);
    parts.push(
      summary.roundTo !== undefined
        ? fmt.tc('assistant.proposal.meta', products, {
            step: fmt.money(summary.roundTo),
          })
        : fmt.tc('assistant.proposal.metaNoRound', products)
    );
  }
  if (summary.branchScope && input.multiBranch)
    parts.push(
      summary.branchScope === 'all'
        ? fmt.t('assistant.proposal.scope.all')
        : fmt.t('assistant.proposal.scope.branch', { branch: view.branchName })
    );
  return parts.length ? clampText(parts.join(' · '), META_MAX) : null;
}

/**
 * The price a row would leave customers paying, which the server flags at
 * zero (plan F6: outliers use what customers pay); of a new product too: its
 * discounted price when it has one.
 */
function priceAfter(row: IAssistantRow): number {
  if (isPriceValue(row.to)) return paidPrice(row.to);
  if (isSizesValue(row.to)) {
    const first = firstActiveSize(row.to);
    return first ? paidPrice(first) : 0;
  }
  return isCreateValue(row.to) ? row.to.discountedPrice ?? row.to.price : 0;
}

/**
 * The price of a product's first available size, which the product's own
 * price mirrors (plan P4-1); null when none is available.
 */
function firstActiveSize(value: ISizesValue): IPriceValue | null {
  const size = value.sizes.find((candidate) => candidate.active);
  return size
    ? { price: size.price, discountedPrice: size.discountedPrice }
    : null;
}

/**
 * Rows of a product whose taken name is a size's (plan P4-1): a new size,
 * and a switch to sizes where two of them share a name.
 */
const SIZE_NAME_FIELDS: readonly TRowField[] = ['newSize', 'sizes'];

/**
 * The text of an outlier reason; a name that is taken says so of the row's
 * entity (plan P4-1): a category (P2-4), an option group (P3-4) or a size,
 * a new size's row and a switch to sizes being their product's.
 */
function outlierReasonKey(
  row: IAssistantRow,
  reason: TRowReason
): string | undefined {
  if (reason !== 'NAME_EXISTS') return OUTLIER_REASON_KEYS[reason];
  const entity = SIZE_NAME_FIELDS.includes(row.field) ? 'size' : rowEntity(row);
  return NAME_EXISTS_KEYS[entity] ?? NAME_EXISTS_KEY;
}

/**
 * Why a row is flagged, under its title: changed since (with what it is now,
 * unless this release cannot read the row; for a row of an option group that
 * now reaches more products, its reach), or an outlier. `group`: the row's
 * entry of the view's `affected`.
 */
function rowReason(
  row: IAssistantRow,
  presenter: IRowPresenter,
  group: IAssistantAffectedGroup | undefined,
  fmt: ICardFormat
): string | null {
  for (const reason of row.reasons) {
    const grown =
      reason === 'CHANGED_SINCE' ? grownGroupText(row, group, fmt) : null;
    if (grown) return clampText(capitalize(grown), REASON_MAX);
    if (reason === 'CHANGED_SINCE' && row.from) {
      if (presenter === GENERIC_PRESENTER) continue;
      return fitText(
        fmt,
        'assistant.proposal.row.stale',
        { price: presenter.text(row.from, row, fmt) ?? EMPTY_TEXT },
        'price',
        REASON_MAX
      );
    }
    if (reason === 'PRICE_ZERO')
      return clampText(
        fmt.t('assistant.proposal.row.priceZero', {
          price: fmt.money(priceAfter(row)),
        }),
        REASON_MAX
      );
    const key = outlierReasonKey(row, reason);
    if (key) return clampText(fmt.t(key), REASON_MAX);
  }
  return null;
}

/** The small picture of a product, when it has one. */
function thumbUrl(picture: string | null, uploadsPath: string): string | null {
  if (!picture) return null;
  const url = `${uploadsPath}/${thumbPath(picture)}`;
  return url.startsWith('https://') && url.length <= THUMB_MAX ? url : null;
}

interface IRowLayout {
  /** A single-row card: the title names the product, the row names the field. */
  single: boolean;
  /**
   * The title names the one subject of every row (a single row, or the
   * fields of one stock item, plan P5-3): each row names its field.
   */
  fieldRows: boolean;
  /** The rows are in several categories, or the card has one row. */
  showCategory: boolean;
  uploadsPath: string;
  stillHidden: IProposalCardInput['stillHidden'];
  /**
   * By row key, the row each row needs that starts unticked (plan P2-4):
   * those rows start unticked too.
   */
  needing: Map<string, IAssistantRow>;
  /**
   * By row key, the changed option group of each group, choice and new-group
   * row: the products that show it are named under the row (plan P3-4).
   */
  usedIn: Map<string, IAssistantAffectedGroup>;
}

/**
 * «Precio», «Precio · Grande»: what a single-row card changes (a size its
 * value names is not repeated). The size is isolated like any value in a
 * sentence, so «1,5 L» keeps its order in Arabic.
 */
function fieldTitle(
  row: IAssistantRow,
  presenter: IRowPresenter,
  fmt: ICardFormat
): string {
  const field = fmt.t(presenter.fieldKey);
  if (!row.sizeName || presenter.namesSize?.(row)) return field;
  const { size } = bidiIsolateParams(fmt.locale, { size: row.sizeName });
  return `${field} · ${size}`;
}

/**
 * A single-row card (or one of a stock item's fields) names the subject in
 * its title, so its rows name the field; a new product, a moved one (titled
 * by where it goes) and a row this release does not know (titled «1 cambio
 * en su menú») name the subject in the row. A new category always names the
 * field: its value is its name (plan P2-4).
 */
function titledByField(row: IAssistantRow, layout: IRowLayout): boolean {
  if (row.field === 'newCategory') return true;
  return layout.fieldRows && !SUBJECT_IN_ROW_FIELDS.includes(row.field);
}

/** A row's title: its field (`titledByField`) or its subject. */
function rowTitle(
  row: IAssistantRow,
  presenter: IRowPresenter,
  layout: IRowLayout,
  fmt: ICardFormat
): string {
  if (presenter === GENERIC_PRESENTER) return rowItemLabel(row);
  return titledByField(row, layout)
    ? fieldTitle(row, presenter, fmt)
    : rowLabel(row, presenter);
}

/**
 * Where the row's subject is, under its title: a product's category (and
 * its menu), a category's menu. A menu, an option group or a stock item
 * belongs to no category, and a move says where in its values.
 */
function rowLocation(row: IAssistantRow): string | null {
  if (row.field === 'category') return null;
  switch (rowEntity(row)) {
    case 'category':
      return row.menuName;
    case 'menu':
      return null;
    default:
      if (!row.categoryName) return null;
      return row.menuName
        ? `${row.categoryName} · ${row.menuName}`
        : row.categoryName;
  }
}

/**
 * The value after of a row that turns on something its category, menu or
 * hours still hide (plan P0-3). The change still applies, so the row stays
 * ticked: «Fuera de horario» when only its hours hide it (it shows at its
 * next hours), «Disponible, no se ve» otherwise. Null: nothing else hides it.
 */
function stillHiddenPill(
  reasons: readonly TStillHiddenReason[] | undefined,
  fmt: ICardFormat
): IGhDiffValue | null {
  if (!reasons?.length) return null;
  return reasons.every((reason) => reason === 'outsideHours')
    ? {
        pill: clampText(fmt.t('menuPage.status.outOfHours'), PILL_MAX),
        tone: 'neutral',
      }
    : {
        pill: clampText(
          fmt.t('assistant.proposal.value.onButHidden'),
          PILL_MAX
        ),
        tone: 'warning',
      };
}

/** What separates the parts of a row's subtitle. */
const SUBTITLE_SEPARATOR = ' · ';

/**
 * What goes under a row's title: where its subject is, what its value adds,
 * the field of a row titled by a subject that does not say what changes
 * («Orden de las variantes» under «Napolitana»), and, for a row of an option
 * group, the products that show it with as many of their names as the rest
 * of the subtitle leaves room for (plan P3-4: the review shows only the
 * rows, so the products must be named there).
 */
function rowSubtitle(
  row: IAssistantRow,
  presenter: IRowPresenter,
  layout: IRowLayout,
  fmt: ICardFormat
): string | null {
  const parts = [
    layout.showCategory ? rowLocation(row) : null,
    presenter.subtitle?.(row, fmt) ?? null,
    presenter.subjectNeedsField && !titledByField(row, layout)
      ? fmt.t(presenter.fieldKey)
      : null,
  ].filter((part): part is string => !!part);
  const group = layout.usedIn.get(row.key);
  if (group) {
    const taken = parts.length
      ? cardTextLength(parts.join(SUBTITLE_SEPARATOR)) +
        SUBTITLE_SEPARATOR.length
      : 0;
    const usedIn =
      SUBTITLE_MAX - taken > 0
        ? usedInText(group, fmt, SUBTITLE_MAX - taken)
        : null;
    if (usedIn) parts.push(usedIn);
  }
  return parts.length
    ? clampText(parts.join(SUBTITLE_SEPARATOR), SUBTITLE_MAX)
    : null;
}

/**
 * How the reason of a row that waits for `required`, or the notice of a
 * ticked row that needs it (plan P4-1), names it (plan P2-4): «Leches ·
 * Leche de soja», a product's size «Fugazzeta · Grande» (a new size by the
 * name in its value), or with its field title when its subject alone does
 * not say what changes («Muzzarella · Orden de las variantes»).
 */
export function requiredRowLabel(
  required: IAssistantRow,
  fmt: ICardFormat
): string {
  const presenter = presenterOf(required);
  if (presenter.namesSize?.(required) && isNewChoiceValue(required.to))
    return rowItemLabel({
      itemName: required.itemName,
      sizeName: required.to.name,
    });
  return rowLabelWithField(required, presenter, fmt);
}

function diffRow(
  row: IAssistantRow,
  layout: IRowLayout,
  fmt: ICardFormat
): IGhDiffRow {
  const presenter = presenterOf(row);
  const diff: IGhDiffRow = {
    key: row.key,
    title: clampText(rowTitle(row, presenter, layout, fmt), ROW_TITLE_MAX),
    // An emptied text reads «—».
    after: stillHiddenPill(layout.stillHidden[row.key], fmt) ??
      presenter.diff(row.to, row, fmt) ?? { text: EMPTY_TEXT },
  };
  const subtitle = rowSubtitle(row, presenter, layout, fmt);
  if (subtitle) diff.subtitle = subtitle;
  const thumb = layout.single
    ? thumbUrl(row.picture, layout.uploadsPath)
    : null;
  if (thumb) diff.thumb = thumb;
  // An empty text before is nothing to strike through.
  const before = row.from ? presenter.diff(row.from, row, fmt) : null;
  if (before) diff.before = before;
  const delta = presenter.delta?.(row, fmt);
  if (delta) diff.delta = delta;
  if (row.state !== 'ok') diff.state = row.state;
  const reason = rowReason(row, presenter, layout.usedIn.get(row.key), fmt);
  if (reason) diff.reason = reason;
  // It needs a row that starts unticked: it starts unticked with it, and
  // says which (plan P2-4; a choice of its option group by «Grupo ·
  // Opción», a size of its product by «Producto · Variante»), so «Aplicar»
  // never applies it alone.
  const required = layout.needing.get(row.key);
  if (required) {
    diff.state = 'outlier';
    diff.reason = fitText(
      fmt,
      'assistant.proposal.row.needsNew',
      { name: requiredRowLabel(required, fmt) },
      'name',
      REASON_MAX
    );
  }
  return diff;
}

/** «Se quita el precio con descuento: sus clientes pagarán el precio nuevo.» */
const DISCOUNT_REMOVED_KEY = 'assistant.proposal.note.discountRemoved';
/** «…el precio normal»: true of every removal, whatever the price does. */
const DISCOUNT_REMOVED_REGULAR_KEY =
  'assistant.proposal.note.discountRemovedRegular';

/** The discount sentences, in the order the note gives them (plan P0-2). */
const DISCOUNT_NOTE_KEYS = [
  'assistant.proposal.note.discountKept',
  DISCOUNT_REMOVED_KEY,
  DISCOUNT_REMOVED_REGULAR_KEY,
  'assistant.proposal.note.discountScaled',
];

/**
 * The regular and discounted price a row changes, before and after: a price
 * row's; a product that switches to sizes, from its price to its first
 * available size's; a product back to one price, from the size it keeps
 * (its first available one) to its new price (plan P4-1: the discount
 * decision of a conversion). Null for any other row.
 */
function priceChangeOf(
  row: IAssistantRow
): { from: IPriceValue; to: IPriceValue } | null {
  const { from, to } = row;
  if (!from) return null;
  if (row.field === 'price')
    return isPriceValue(from) && isPriceValue(to) ? { from, to } : null;
  if (row.field !== 'sizes') return null;
  if (isPriceValue(from) && isSizesValue(to)) {
    const first = firstActiveSize(to);
    return first ? { from, to: first } : null;
  }
  if (isSizesValue(from) && isPriceValue(to)) {
    const kept = firstActiveSize(from);
    return kept ? { from: kept, to } : null;
  }
  return null;
}

/**
 * What a row does to a discounted price that customers pay (a price row, or
 * a product's switch to sizes or back to one price, plan P4-1): it is
 * removed (customers then pay the new price, or the regular one when the
 * price stays), or, while the regular price changes, it stays or moves too.
 * Null for any other row.
 */
function discountNoteKey(row: IAssistantRow): string | null {
  const change = priceChangeOf(row);
  if (!change || change.from.discountedPrice === null) return null;
  const { from, to } = change;
  if (to.discountedPrice === null)
    return to.price === from.price
      ? DISCOUNT_REMOVED_REGULAR_KEY
      : DISCOUNT_REMOVED_KEY;
  if (to.price === from.price) return null;
  if (to.discountedPrice === from.discountedPrice)
    return 'assistant.proposal.note.discountKept';
  return 'assistant.proposal.note.discountScaled';
}

/**
 * What a category row's own reasons read (plan P2-4): its own «solo del
 * Punto de Venta» is «es solo del Punto de Venta»; its hours and its menu
 * have twins of the P0 words, so languages whose pronouns agree with
 * «categoría» read right.
 */
const CATEGORY_REASON_KEYS: Readonly<
  Partial<Record<TStillHiddenReason, string>>
> = {
  categoryPosOnly: 'assistant.hiddenReason.posOnly',
  outsideHours: 'assistant.hiddenReason.outsideHoursCategory',
  menuNotOffered: 'assistant.hiddenReason.menuNotOfferedCategory',
};

/** Why `row` stays hidden: a category's own words, else the P0 words. */
function hiddenReasonKey(
  row: IAssistantRow,
  reason: TStillHiddenReason
): string {
  const categoryKey =
    rowEntity(row) === 'category' ? CATEGORY_REASON_KEYS[reason] : undefined;
  return categoryKey ?? `assistant.hiddenReason.${reason}`;
}

/**
 * «Flan sigue sin verse: su categoría está oculta.», «La categoría Postres
 * sigue oculta: es solo del Punto de Venta.»: the sentence of one row whose
 * subject stays hidden, fit to `room` by shortening the name, never the
 * reason; when even a name of NOTE_NAME_MIN characters does not fit, it
 * comes back whole and the caller leaves it out.
 */
function stillHiddenOneSentence(
  row: IAssistantRow,
  stillHidden: IProposalCardInput['stillHidden'],
  fmt: ICardFormat,
  room: number
): string {
  const [reason] = stillHidden[row.key];
  const key =
    rowEntity(row) === 'category'
      ? 'assistant.proposal.note.stillHiddenCategory'
      : 'assistant.proposal.note.stillHiddenOne';
  const params = {
    name: row.itemName,
    reason: fmt.t(hiddenReasonKey(row, reason)),
  };
  const sentence = fmt.t(key, params);
  const length = cardTextLength(sentence);
  if (length <= room) return sentence;
  const shortest =
    length - Math.max(0, cardTextLength(row.itemName) - NOTE_NAME_MIN);
  return shortest > room ? sentence : fitText(fmt, key, params, 'name', room);
}

/**
 * What stays hidden after the change, of `rows` (never empty, each with
 * reasons in `stillHidden`; the proposal's note and the receipt, plan P0-3,
 * P2-4): the sentence of the one row; «{n} categorías siguen ocultas…» for
 * several categories; with products among several, the products only (their
 * one sentence, or «{n} productos siguen ocultos…»): each row still shows
 * its pill.
 */
export function stillHiddenSentence(
  rows: IAssistantRow[],
  stillHidden: IProposalCardInput['stillHidden'],
  fmt: ICardFormat,
  room: number
): string {
  if (rows.length === 1)
    return stillHiddenOneSentence(rows[0], stillHidden, fmt, room);
  const products = rows.filter((row) => rowEntity(row) !== 'category');
  if (!products.length)
    return fmt.t('assistant.proposal.note.stillHiddenCategories', {
      n: fmt.number(rows.length),
    });
  if (products.length === 1)
    return stillHiddenOneSentence(products[0], stillHidden, fmt, room);
  return fmt.t('assistant.proposal.note.stillHiddenMany', {
    n: fmt.number(products.length),
  });
}

/**
 * A row creates something customers do not see at once: a product or a
 * category created hidden, a product created in a category this proposal
 * creates hidden (`rowOf`: the proposal's rows by key), or a copy of a
 * product (it starts hidden in every branch, plan P4-3).
 */
function createsHidden(
  row: IAssistantRow,
  rowOf: ReadonlyMap<string, IAssistantRow>
): boolean {
  const { to } = row;
  if (row.field === 'copy') return true;
  if (row.field === 'newCategory') return isNewCategoryValue(to) && !to.visible;
  if (row.field !== 'create' || !isCreateValue(to)) return false;
  if (to.visible === false) return true;
  const category = to.categoryKey ? rowOf.get(to.categoryKey) : undefined;
  return category?.field === 'newCategory' && createsHidden(category, rowOf);
}

/**
 * «La categoría nueva se quita al deshacer si nadie le agregó otros
 * productos.» after a lead that speaks of other changes (plan P2-4: until
 * when a new category can be undone; the lead already says it can be).
 */
function newCategoryUndoSentences(
  rows: IAssistantRow[],
  fmt: ICardFormat
): string[] {
  const categories = rows.filter((row) => row.field === 'newCategory').length;
  return categories
    ? [fmt.tc('assistant.proposal.note.categoryUndo', categories)]
    : [];
}

/** A row whose schedule leaves its subject hours of its own (not «always»). */
function setsHours(row: IAssistantRow): boolean {
  return isScheduleValue(row.to) && !!row.to.hourRanges?.length;
}

/**
 * A product that switches to sizes (plan P4-1): its price is then its first
 * available size's. Back to one price it has no sizes left.
 */
function switchesToSizes(row: IAssistantRow): boolean {
  return row.field === 'sizes' && isSizesValue(row.to);
}

/**
 * «Solo cambia el inventario de Palermo. Puede deshacerlo.»: an inventory
 * proposal changes only its branch's stock, which customers never see (plan
 * P5-3).
 */
function stockBranchSentence(
  view: IAssistantProposalView,
  fmt: ICardFormat
): string {
  return fitText(
    fmt,
    'assistant.proposal.note.stockBranch',
    { branch: view.branchName },
    'branch',
    NOTE_MAX
  );
}

/**
 * That a new unit converts nothing (plan P5-3), when a row changes a stock
 * item's unit.
 */
function stockUnitSentences(
  view: IAssistantProposalView,
  fmt: ICardFormat
): string[] {
  return view.rows.some(
    (row) => row.field === 'unit' && rowEntity(row) === 'stockItem'
  )
    ? [fmt.t('assistant.proposal.note.stockUnit')]
    : [];
}

/**
 * The note's first sentences of a change of the menu, by what every row
 * does (plan P0, P2-4, P4). Creations speak of what customers see at once:
 * new products (then until when new categories can be undone), else the
 * new categories they see,
 * else that copies stay hidden until shown (plan P4-3), and only that it
 * can be undone (everything is created hidden). Preparation notes are seen
 * by the staff only (plan P4-4); hours say that outside them the menu hides
 * it (plan P4-2). A new order says where it shows; many prices say
 * customers see them; a product whose price follows another size, or a new
 * price of it (`followsSize`: a switch to sizes, or a size's switch,
 * removal, order or price), says which price the product has (plan P4-1);
 * anything else that customers see it at once (then until when new
 * categories can be undone). Every lead says it can be undone, so nothing
 * after it says so again.
 */
function menuLeadSentences(
  view: IAssistantProposalView,
  followsSize: boolean,
  fmt: ICardFormat
): string[] {
  const { rows } = view;
  const { code } = view.summary;
  const every = (fields: readonly TRowField[]) =>
    rows.length > 0 && rows.every((row) => fields.includes(row.field));
  if (code === 'createItem' || every(CREATION_FIELDS)) {
    const rowOf = new Map(rows.map((row) => [row.key, row]));
    const shown = rows.filter((row) => !createsHidden(row, rowOf));
    // No rows to read: a createItem summary creates products.
    if (!rows.length || shown.some((row) => row.field === 'create'))
      return [
        fmt.t('assistant.proposal.note.create'),
        ...newCategoryUndoSentences(rows, fmt),
      ];
    const categories = shown.filter((row) => row.field === 'newCategory');
    if (categories.length)
      return [
        fmt.tc('assistant.proposal.note.categoryCreate', categories.length),
      ];
    return rows.some((row) => row.field === 'copy')
      ? [fmt.t(COPY_HIDDEN_KEY), fmt.t(UNDO_KEY)]
      : [fmt.t(UNDO_KEY)];
  }
  if (every(['notes'])) return [fmt.t('assistant.proposal.note.staffOnly')];
  // Back to «always», customers see it whenever the store sells.
  if (every(['schedule']) && rows.some(setsHours)) return [fmt.t(SCHEDULE_KEY)];
  if (every(['order']))
    return [fmt.t('assistant.proposal.note.order'), fmt.t(UNDO_KEY)];
  if (rows.length > 1 && PRICE_CODES.includes(code))
    return [fmt.t('assistant.proposal.note.prices')];
  // «Solo en el Punto de Venta» takes the category off the menu: «customers
  // see it at once on the menu» would say the opposite.
  if (
    rows.length > 0 &&
    rows.every(
      (row) => row.field === 'posOnly' && isFlagValue(row.to) && row.to.on
    )
  )
    return [fmt.t(UNDO_KEY)];
  if (followsSize)
    return [fmt.t(SIZES_KEY), ...newCategoryUndoSentences(rows, fmt)];
  return [
    fmt.t('assistant.proposal.note.single'),
    ...newCategoryUndoSentences(rows, fmt),
  ];
}

/** An inventory proposal says whose stock it changes (plan P5-3). */
function stockLeadSentences(
  view: IAssistantProposalView,
  _followsSize: boolean,
  fmt: ICardFormat
): string[] {
  return [stockBranchSentence(view, fmt)];
}

/** The note's first sentences of one kind's proposal. */
type TLeadSentences = (
  view: IAssistantProposalView,
  followsSize: boolean,
  fmt: ICardFormat
) => string[];

const LEAD_SENTENCES: Readonly<Record<TAssistantKind, TLeadSentences>> = {
  'menu.changes': menuLeadSentences,
  'stock.changes': stockLeadSentences,
};

/** The note's first sentences, by the proposal's kind (`LEAD_SENTENCES`). */
function leadSentences(
  view: IAssistantProposalView,
  followsSize: boolean,
  fmt: ICardFormat
): string[] {
  return ofKind(LEAD_SENTENCES, view.kind)(view, followsSize, fmt);
}

/**
 * A copy whose source another row of the proposal changes: the copy is made
 * before the proposal's other changes (plan F8), so it does not get them.
 */
function copiesChangedSource(rows: readonly IAssistantRow[]): boolean {
  // A copy's own row has no product yet (`itemId` null).
  const changed = new Set(
    rows.flatMap((row) => (row.itemId !== null ? [row.itemId] : []))
  );
  return rows.some(
    (row) =>
      row.field === 'copy' &&
      isCopyValue(row.to) &&
      changed.has(row.to.sourceItemId)
  );
}

/**
 * About copies among other changes (plan P4-3): that a copy is the product
 * as it is now when the proposal also changes it (nothing else on the card
 * says so), then that a copy stays hidden until shown, after a lead that
 * does not say it already (its row's pill says it too).
 */
function copySentences(
  view: IAssistantProposalView,
  lead: readonly string[],
  fmt: ICardFormat
): string[] {
  if (!view.rows.some((row) => row.field === 'copy')) return [];
  const copyHidden = fmt.t(COPY_HIDDEN_KEY);
  return [
    ...(copiesChangedSource(view.rows)
      ? [fmt.t('assistant.proposal.note.copySource')]
      : []),
    ...(lead.includes(copyHidden) ? [] : [copyHidden]),
  ];
}

/**
 * That outside the new hours customers do not see it (plan P4-2), after a
 * lead that does not say it already (hours among other changes).
 */
function scheduleSentences(
  view: IAssistantProposalView,
  lead: readonly string[],
  fmt: ICardFormat
): string[] {
  return view.rows.some(setsHours) && !lead.includes(fmt.t(SCHEDULE_KEY))
    ? [fmt.t('assistant.proposal.note.scheduleOutside')]
    : [];
}

/**
 * That a product's price follows its first size on sale (plan P4-1), after a
 * lead that does not say it already (a new order or many prices); said
 * last, so it never takes the room of a sentence that matters more.
 */
function sizesSentences(
  followsSize: boolean,
  sentences: readonly string[],
  fmt: ICardFormat
): string[] {
  return followsSize && !sentences.includes(fmt.t(SIZES_KEY))
    ? [fmt.t('assistant.proposal.note.sizesFirst')]
    : [];
}

/**
 * What a category change adds after the lead (plan P2-4): a category's
 * switch also holds for the products added later.
 */
function categoryNoteKeys(view: IAssistantProposalView): string[] {
  return view.rows.some(
    (row) => row.field === 'availability' && rowEntity(row) === 'category'
  )
    ? ['assistant.proposal.note.categoryAvailability']
    : [];
}

/**
 * What a change of option groups adds after the lead (plan P3-4, D3): that
 * it changes in every product using a shared group, and in how many products
 * the group keeps rules of its own.
 */
function groupNoteSentences(
  view: IAssistantProposalView,
  fmt: ICardFormat
): string[] {
  const affected = view.affected ?? [];
  const sentences: string[] = [];
  if (changesSharedGroup(affected))
    sentences.push(fmt.t('assistant.proposal.note.groupShared'));
  const ownRules = ownRulesCount(affected);
  if (ownRules > 0)
    sentences.push(
      fmt.tc('assistant.proposal.note.groupOwnRules', ownRules, {
        n: fmt.number(ownRules),
      })
    );
  return sentences;
}

/**
 * What was left out and can be done another way, said once each (plan P0-6,
 * P2-4): prices that cannot change this way, new products not created because
 * of their discounted price, and products hidden in every branch; a proposal
 * that speaks of changes counts its prices and categories left out as
 * changes.
 */
function excludedSentences(
  view: IAssistantProposalView,
  fmt: ICardFormat
): string[] {
  const priceRows = view.excluded.filter((row) =>
    PRICE_EXCLUSIONS.includes(row.reason)
  );
  // A new product has no price to change: it is not created.
  const creates = priceRows.filter(
    (row) => row.reason === 'DISCOUNT_INVALID' && isNewProductExclusion(row)
  ).length;
  const prices = priceRows.length - creates;
  const hidden = view.excluded.filter(
    (row) => row.reason === 'HIDDEN_EVERYWHERE'
  );
  const hiddenCategories = hidden.filter(isCategoryExclusion).length;
  const hiddenProducts = hidden.length - hiddenCategories;
  const sentences: string[] = [];
  if (speaksOfChanges(view)) {
    const changes = prices + hiddenCategories;
    if (changes)
      sentences.push(fmt.tc('assistant.proposal.note.excludedAny', changes));
  } else if (prices) {
    sentences.push(fmt.tc('assistant.proposal.note.excluded', prices));
  }
  if (creates)
    sentences.push(fmt.tc('assistant.proposal.note.excludedCreate', creates));
  if (hiddenProducts)
    sentences.push(
      fmt.tc('assistant.proposal.note.hiddenEverywhere', hiddenProducts)
    );
  return sentences;
}

/**
 * «Sus clientes lo ven al instante…» (or the lead of what every row does),
 * then, while each fits the note (never cut): that a copy is the product as
 * it is now and stays hidden until shown, that outside new hours it does
 * not show, that a new stock unit converts nothing, that a shared option
 * group changes in every product using it, what a category change adds,
 * translations, what was left out (shown nowhere else), what still hides
 * what the rows turn on (each such row shows a pill, not why), what happens
 * to discounted prices (each row also shows it), and that a product's price
 * is its first available size's when the lead did not say it. When every
 * row turns on something still hidden, customers do not see it at once: the
 * note starts with why it stays hidden and that it can be undone.
 */
function proposalNote(
  input: IProposalCardInput,
  fmt: ICardFormat
): IGhDiffNote {
  const { view } = input;
  const hiddenRows = view.rows.filter(
    (row) => input.stillHidden[row.key]?.length
  );
  const allHidden =
    hiddenRows.length > 0 && hiddenRows.length === view.rows.length;
  const followsSize =
    view.rows.some(switchesToSizes) || input.firstSizeChangedIds.length > 0;
  const sentences = allHidden
    ? [
        stillHiddenSentence(hiddenRows, input.stillHidden, fmt, NOTE_MAX),
        fmt.t(UNDO_KEY),
      ]
    : leadSentences(view, followsSize, fmt);
  sentences.push(...copySentences(view, sentences, fmt));
  sentences.push(...scheduleSentences(view, sentences, fmt));
  sentences.push(...stockUnitSentences(view, fmt));
  sentences.push(...groupNoteSentences(view, fmt));
  for (const key of categoryNoteKeys(view)) sentences.push(fmt.t(key));
  if (view.notes.includes('TRANSLATIONS_UNCHANGED'))
    sentences.push(fmt.t('assistant.proposal.note.translations'));
  sentences.push(...excludedSentences(view, fmt));
  if (hiddenRows.length && !allHidden)
    sentences.push(
      stillHiddenSentence(
        hiddenRows,
        input.stillHidden,
        fmt,
        sentenceRoom(fmt.locale, sentences, NOTE_MAX)
      )
    );
  const discounts = new Set(view.rows.map(discountNoteKey));
  // One removal sentence: «…el precio nuevo» only when every removal
  // changes the price.
  if (discounts.has(DISCOUNT_REMOVED_REGULAR_KEY))
    discounts.delete(DISCOUNT_REMOVED_KEY);
  for (const key of DISCOUNT_NOTE_KEYS)
    if (discounts.has(key)) sentences.push(fmt.t(key));
  sentences.push(...sizesSentences(followsSize, sentences, fmt));
  return { text: fitSentences(fmt.locale, sentences, NOTE_MAX), icon: 'eye' };
}

/** «Descartar». */
export function discardAction(
  payload: IGhPayload,
  fmt: ICardFormat
): IGhHostAction {
  return {
    id: 'discard',
    kind: 'host',
    label: clampText(fmt.t('assistant.proposal.discard'), LABEL_MAX),
    style: 'outline',
    pendingLabel: clampText(fmt.t('assistant.proposal.discarding'), LABEL_MAX),
    payload,
  };
}

/** «Revisar de nuevo» / «Preparar de nuevo»: the proposal against the live menu. */
export function refreshAction(
  payload: IGhPayload,
  labelKey: 'assistant.result.retry' | 'assistant.result.prepareAgain',
  fmt: ICardFormat
): IGhHostAction {
  return {
    id: 'refresh',
    kind: 'host',
    label: clampText(fmt.t(labelKey), LABEL_MAX),
    icon: 'refresh',
    style: 'success',
    pendingLabel: clampText(fmt.t('assistant.result.refreshing'), LABEL_MAX),
    payload,
  };
}

function applyAction(
  payload: IGhPayload,
  count: number,
  fmt: ICardFormat
): IGhHostAction {
  return {
    id: 'apply',
    kind: 'host',
    label: clampText(fmt.t('assistant.proposal.apply'), LABEL_MAX),
    icon: 'check',
    style: 'success',
    pendingLabel: clampText(
      fmt.tc('assistant.proposal.applying', count),
      LABEL_MAX
    ),
    payload,
  };
}

/**
 * «Cambiar algo»: fills the composer with what the card is about (the
 * product of a single row, else the card title), so it reads «Sobre: …».
 */
function changeAction(
  view: IAssistantProposalView,
  title: string,
  fmt: ICardFormat
): IGhPromptAction {
  const subject = view.rows.length === 1 ? view.rows[0].itemName : title;
  return {
    id: 'change',
    kind: 'prompt',
    label: clampText(fmt.t('assistant.proposal.change'), LABEL_MAX),
    style: 'link',
    text: clampText(subject, PROMPT_TEXT_MAX),
    send: false,
  };
}

function reviewAction(count: number, fmt: ICardFormat): IGhExpandAction {
  return {
    id: 'review',
    kind: 'expand',
    label: clampText(fmt.tc('assistant.proposal.review', count), LABEL_MAX),
    icon: 'checklist',
    style: 'success',
  };
}

/** The review's confirm button: «Cambiar {n} precios», «Ocultar {n} productos»… */
function applyLabelKey(view: IAssistantProposalView): string {
  const { code, available } = view.summary;
  if (PRICE_CODES.includes(code)) return 'assistant.proposal.applyLabel.price';
  if (code === 'setAvailability' && available === false)
    return 'assistant.proposal.applyLabel.hide';
  if (code === 'setAvailability' && available === true)
    return 'assistant.proposal.applyLabel.show';
  if (code === 'createItem') return 'assistant.proposal.applyLabel.create';
  return 'assistant.proposal.applyLabel.mixed';
}

/**
 * Why rows start unticked: changed since, or check them before applying (of
 * products, or of changes when the proposal changes more, plan P2-4). A row
 * that needs a flagged one starts unticked with it: that flagged row already
 * shows the banner.
 */
function flaggedBanner(
  view: IAssistantProposalView,
  fmt: ICardFormat
): string | null {
  const changes = rowsAreChanges(view);
  if (view.rows.some((row) => row.state === 'stale'))
    return clampText(
      fmt.t(
        changes
          ? 'assistant.proposal.banner.staleAny'
          : 'assistant.proposal.banner.stale'
      ),
      BANNER_MAX
    );
  if (view.tainted || view.rows.some((row) => row.state === 'outlier'))
    return clampText(
      fmt.t(
        changes
          ? 'assistant.proposal.banner.flaggedAny'
          : 'assistant.proposal.banner.flagged'
      ),
      BANNER_MAX
    );
  return null;
}

/**
 * The review's hint: untick a change (plan P2-4), or a product, or the new
 * products one does not want.
 */
function reviewHintKey(view: IAssistantProposalView): string {
  if (rowsAreChanges(view)) return 'assistant.proposal.hintAny';
  return view.summary.code === 'createItem'
    ? 'assistant.proposal.hintCreate'
    : 'assistant.proposal.hint';
}

/**
 * The review's summary: what the proposal does, that a shared option group
 * changes in every product using it (plan P3-4: the review shows no note and
 * no other card), and how to leave a change out.
 */
function reviewSummary(
  view: IAssistantProposalView,
  title: string,
  fmt: ICardFormat
): string {
  const { summary } = view;
  const lead =
    summary.roundTo !== undefined
      ? fmt.t('assistant.proposal.summaryRound', {
          title,
          step: fmt.money(summary.roundTo),
        })
      : fmt.t('assistant.proposal.summary', { title });
  const shared = changesSharedGroup(view.affected ?? [])
    ? [fmt.t('assistant.proposal.note.groupShared')]
    : [];
  const hint = fmt.t(reviewHintKey(view));
  return clampText(
    joinSentences(fmt.locale, [lead, ...shared, hint]),
    SUMMARY_MAX
  );
}

function proposalReview(
  view: IAssistantProposalView,
  title: string,
  payload: IGhPayload,
  fmt: ICardFormat
): IGhDiffReview {
  const review: IGhDiffReview = {
    summary: reviewSummary(view, title, fmt),
    confirm: {
      id: 'apply',
      payload,
      label: hostPlural(fmt, applyLabelKey(view)),
      pendingLabel: clampText(
        fmt.t('assistant.proposal.applyingAny'),
        LABEL_MAX
      ),
    },
    cancelLabel: clampText(fmt.t('assistant.proposal.cancel'), LABEL_MAX),
    emptySelectionText: clampText(
      fmt.t('assistant.proposal.noneSelected'),
      EMPTY_SELECTION_MAX
    ),
  };
  const banner = flaggedBanner(view, fmt);
  if (banner) review.flaggedBanner = banner;
  return review;
}

/** Everything of a proposal's diff card but its note, review and actions. */
function diffBase(
  input: IProposalCardInput,
  fmt: ICardFormat
): Omit<IGhDiffCard, 'actions'> {
  const { view } = input;
  const { rows } = view;
  const single = rows.length === 1;
  const layout: IRowLayout = {
    single,
    fieldRows: single || editsOneStockItem(rows),
    // A product of a category this proposal creates has no category id
    // yet: its category's name tells it apart.
    showCategory:
      single ||
      rows.some(
        (row) =>
          row.categoryId !== rows[0].categoryId ||
          row.categoryName !== rows[0].categoryName
      ),
    uploadsPath: input.uploadsPath,
    stillHidden: input.stillHidden,
    needing: rowsNeedingUnticked(rows),
    usedIn: affectedGroupByRow(rows, view.affected ?? []),
  };
  const card: Omit<IGhDiffCard, 'actions'> = {
    id: proposalCardId(view.id),
    v: 1,
    type: 'diff',
    eyebrow: clampText(
      fmt.tc('menuImport.proposal.label', view.rowCount, {
        count: view.rowCount,
      }),
      EYEBROW_MAX
    ),
    title: proposalTitle(view, fmt),
    rows: rows.map((row) => diffRow(row, layout, fmt)),
  };
  const meta = proposalMeta(input, fmt);
  if (meta) card.meta = meta;
  if (view.tainted)
    card.badge = {
      text: clampText(fmt.t('assistant.proposal.badge.tainted'), LABEL_MAX),
      tone: 'neutral',
    };
  const request = input.requestText?.trim();
  if (request) card.request = clampText(request, REQUEST_MAX);
  if (rows.length > MORE_AFTER_ROWS)
    card.moreLabel = hostPlural(
      fmt,
      rowsAreChanges(view)
        ? 'assistant.proposal.moreAny'
        : 'assistant.proposal.more'
    );
  return card;
}

/**
 * The proposal's diff card (plan §9.9): inline with «Aplicar», «Descartar»
 * and «Cambiar algo»; or collapsed with «Revisar los {n} cambios» (the
 * review, where flagged rows start unticked) and «Descartar».
 */
export function proposalCard(
  input: IProposalCardInput,
  fmt: ICardFormat
): IGhDiffCard {
  const { view } = input;
  const base = diffBase(input, fmt);
  const payload = proposalPayload(view);
  const note = proposalNote(input, fmt);
  if (proposalMode(view) === 'inline')
    return {
      ...base,
      note,
      actions: [
        applyAction(payload, view.rowCount, fmt),
        discardAction(payload, fmt),
        changeAction(view, base.title, fmt),
      ],
    };
  return {
    ...base,
    note,
    review: proposalReview(view, base.title, payload, fmt),
    actions: [reviewAction(view.rowCount, fmt), discardAction(payload, fmt)],
  };
}

/** The proposal after its 30 minutes: «Esta propuesta venció…» and «Preparar de nuevo». */
export function expiredProposalCard(
  input: IProposalCardInput,
  fmt: ICardFormat
): IGhDiffCard {
  return {
    ...diffBase(input, fmt),
    note: {
      text: clampText(fmt.t('assistant.result.expired'), NOTE_MAX),
      icon: 'hourglass',
    },
    actions: [
      refreshAction(
        proposalPayload(input.view),
        'assistant.result.prepareAgain',
        fmt
      ),
    ],
  };
}
